// Toast system: transient notes that also persist to the notes feed later.
// Errors persist until dismissed; everything else breathes out on its own.

import { create } from "zustand";
import { AnimatePresence, motion } from "framer-motion";
import { newId } from "@/lib/ids";
import { springUi, durations, easeIn } from "@/motion/springs";
import { soundGentleError } from "@/sounds/ui";

export interface ToastItem {
  id: string;
  text: string;
  kind: "note" | "success" | "error";
}

interface ToastStore {
  items: ToastItem[];
  push: (text: string, kind?: ToastItem["kind"]) => void;
  dismiss: (id: string) => void;
}

export const useToasts = create<ToastStore>((set) => ({
  items: [],
  push: (text, kind = "note") => {
    const id = newId();
    if (kind === "error") soundGentleError();
    set((s) => ({ items: [...s.items.slice(-2), { id, text, kind }] }));
    if (kind !== "error") {
      setTimeout(() => set((s) => ({ items: s.items.filter((t) => t.id !== id) })), 4200);
    }
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

export function toast(text: string, kind: ToastItem["kind"] = "note"): void {
  useToasts.getState().push(text, kind);
}

export function ToastHost() {
  const { items, dismiss } = useToasts();
  return (
    <div className="bd-toasts" aria-live="polite">
      <AnimatePresence>
        {items.map((t) => (
          <motion.div
            key={t.id}
            className={`bd-toast${t.kind !== "note" ? ` bd-toast--${t.kind}` : ""}`}
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: springUi }}
            exit={{ opacity: 0, y: 8, transition: { duration: durations.fast, ease: easeIn } }}
            role={t.kind === "error" ? "alert" : "status"}
            onClick={() => dismiss(t.id)}
          >
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
